import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import useStyles from "./CreditRatingView.styles";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import WhatWeDo from "../../whatWeDo/WhatWeDo.component";
import { useCreditRatingContext } from "../../../context/CreditRatingContext";
import {
  Box,
  TextField,
  InputLabel,
  Card,
  Typography,
  Skeleton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CreditRatingExport from "./CreditRatingExport";
import { capitalizeFirstLetterOfEachWord } from "../../../utils/utils";
import MultiSelectOption from "../../common/MultiSelectOption/MultiSelectOption";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const columns = [
  { id: "name", label: "Name", minWidth: 120 },
  { id: "ticker", label: "Ticker", minWidth: 120 },
  // sp rating
  {
    label: "S&P Ratings",
    children: [
      {
        id: "RTG_SP_ST_LC_ISSUER_CREDIT",
        label: "ST Issuer Credit Rating",
        minWidth: 60,
      },
      {
        id: "RTG_SP_ST_LC_ISS_CRED_RTG_DT",
        label: "ST Issuer Credit Rating Dt",
        minWidth: 65,
      },
      {
        id: "RTG_SP_LT_LC_ISSUER_CREDIT",
        label: "LT Issuer Credit Rating",
        minWidth: 60,
      },
      {
        id: "RTG_SP_LT_LC_ISS_CRED_RTG_DT",
        label: "LT Issuer Credit Rating Dt",
        minWidth: 65,
      },
      { id: "RTG_SP_OUTLOOK", label: "Outlook", minWidth: 60 },
      { id: "RTG_SP_OUTLOOK_DT", label: "Outlook Dt", minWidth: 65 },
    ],
  },
  {
    label: "Moody's Ratings",
    children: [
      {
        id: "RTG_MDY_SHORT_TERM_DEBT",
        label: "ST Debt Rating",
        minWidth: 50,
      },
      { id: "RTG_MDY_ST_RTG_DT", label: "ST Debt Rating Dt", minWidth: 65 },
      {
        id: "RTG_MDY_SEN_UNSECURED_DEBT",
        label: "Sr Unsec Debt Rating",
        minWidth: 60,
      },
      {
        id: "RTG_MDY_SEN_UNSEC_RTG_DT",
        label: "Sr Unsec Debt Rating Dt",
        minWidth: 65,
      },
      { id: "RTG_MDY_ISSUER", label: "Issuer Rating", minWidth: 50 },
      {
        id: "RTG_MDY_ISSUER_RTG_DT",
        label: "Issuer Rating Dt",
        minWidth: 65,
      },
      {
        id: "RTG_MDY_LC_CURR_ISSUER_RATING",
        label: "Issuer Rating (Local)",
        minWidth: 50,
      },
      {
        id: "RTG_MDY_LC_CURR_ISSUER_RTG_DT",
        label: "Issuer Rating (Local) Dt",
        minWidth: 65,
      },
      { id: "RTG_MDY_OUTLOOK", label: "Outlook", minWidth: 60 },
      { id: "RTG_MDY_OUTLOOK_DT", label: "Outlook Dt", minWidth: 65 },
    ],
  },
  {
    label: "Fitch Ratings",
    children: [
      {
        id: "RTG_FITCH_SHORT_TERM",
        label: "ST Rating",
        minWidth: 50,
      },
      { id: "RTG_FITCH_ST_RTG_DT", label: "ST Rating Dt", minWidth: 65 },
      {
        id: "RTG_FITCH_SEN_UNSECURED",
        label: "Sr Unsec Debt Rating",
        minWidth: 60,
      },
      {
        id: "RTG_FITCH_SEN_UNSEC_RTG_DT",
        label: "Sr Unsec Debt Rating Dt",
        minWidth: 65,
      },
      { id: "RTG_FITCH_OUTLOOK", label: "Outlook", minWidth: 60 },
      {
        id: "RTG_FITCH_OUTLOOK_DT",
        label: "Outlook Dt",
        minWidth: 65,
      },
    ],
  },
];
function createData(name, ticker, fieldInfo) {
  const {
    // sp rating
    RTG_SP_ST_LC_ISSUER_CREDIT,
    RTG_SP_ST_LC_ISS_CRED_RTG_DT,
    RTG_SP_LT_LC_ISSUER_CREDIT,
    RTG_SP_LT_LC_ISS_CRED_RTG_DT,
    RTG_SP_OUTLOOK,
    RTG_SP_OUTLOOK_DT,
    // moody
    RTG_MDY_SHORT_TERM_DEBT,
    RTG_MDY_ST_RTG_DT,
    RTG_MDY_SEN_UNSECURED_DEBT,
    RTG_MDY_SEN_UNSEC_RTG_DT,
    RTG_MDY_ISSUER,
    RTG_MDY_ISSUER_RTG_DT,
    RTG_MDY_LC_CURR_ISSUER_RATING,
    RTG_MDY_LC_CURR_ISSUER_RTG_DT,
    RTG_MDY_OUTLOOK,
    RTG_MDY_OUTLOOK_DT,
    //fitch rating
    RTG_FITCH_SHORT_TERM,
    RTG_FITCH_ST_RTG_DT,
    RTG_FITCH_SEN_UNSECURED,
    RTG_FITCH_SEN_UNSEC_RTG_DT,
    RTG_FITCH_OUTLOOK,
    RTG_FITCH_OUTLOOK_DT,
  } = fieldInfo || {};

  return {
    name,
    ticker,
    // sp rating
    RTG_SP_ST_LC_ISSUER_CREDIT: RTG_SP_ST_LC_ISSUER_CREDIT || "N/A",
    RTG_SP_ST_LC_ISS_CRED_RTG_DT: RTG_SP_ST_LC_ISS_CRED_RTG_DT || "N/A",
    RTG_SP_LT_LC_ISSUER_CREDIT: RTG_SP_LT_LC_ISSUER_CREDIT || "N/A",
    RTG_SP_LT_LC_ISS_CRED_RTG_DT: RTG_SP_LT_LC_ISS_CRED_RTG_DT || "N/A",
    RTG_SP_OUTLOOK: RTG_SP_OUTLOOK || "N/A",
    RTG_SP_OUTLOOK_DT: RTG_SP_OUTLOOK_DT || "N/A",
    // moody
    RTG_MDY_SHORT_TERM_DEBT: RTG_MDY_SHORT_TERM_DEBT || "N/A",
    RTG_MDY_ST_RTG_DT: RTG_MDY_ST_RTG_DT || "N/A",
    RTG_MDY_SEN_UNSECURED_DEBT: RTG_MDY_SEN_UNSECURED_DEBT || "N/A",
    RTG_MDY_SEN_UNSEC_RTG_DT: RTG_MDY_SEN_UNSEC_RTG_DT || "N/A",
    RTG_MDY_ISSUER: RTG_MDY_ISSUER || "N/A",
    RTG_MDY_ISSUER_RTG_DT: RTG_MDY_ISSUER_RTG_DT || "N/A",
    RTG_MDY_LC_CURR_ISSUER_RATING: RTG_MDY_LC_CURR_ISSUER_RATING || "N/A",
    RTG_MDY_LC_CURR_ISSUER_RTG_DT: RTG_MDY_LC_CURR_ISSUER_RTG_DT || "N/A",
    RTG_MDY_OUTLOOK: RTG_MDY_OUTLOOK || "N/A",
    RTG_MDY_OUTLOOK_DT: RTG_MDY_OUTLOOK_DT || "N/A",
    //fitch rating
    RTG_FITCH_SHORT_TERM: RTG_FITCH_SHORT_TERM || "N/A",
    RTG_FITCH_ST_RTG_DT: RTG_FITCH_ST_RTG_DT || "N/A",
    RTG_FITCH_SEN_UNSECURED: RTG_FITCH_SEN_UNSECURED || "N/A",
    RTG_FITCH_SEN_UNSEC_RTG_DT: RTG_FITCH_SEN_UNSEC_RTG_DT || "N/A",
    RTG_FITCH_OUTLOOK: RTG_FITCH_OUTLOOK || "N/A",
    RTG_FITCH_OUTLOOK_DT: RTG_FITCH_OUTLOOK_DT || "N/A",
  };
}

const mapName = (value) => ({
  id: value,
  name: value,
});

const CreditRatingView = () => {
  const { classes } = useStyles();
  const {
    selectedReportDate,
    setSelectedReportDate,

    creditRatingFiltersLoading,
    creditRatingFilters,

    creditRatingDataLoading,
    creditRatingDataError,
    creditRatingData,
  } = useCreditRatingContext();

  const {
    dateWithNames = [],
    minStartDate,
    maxEndDate,
  } = creditRatingFilters || {};

  const [selectedNames, setSelectedNames] = useState([]);

  const [filteredReportDatas, setFilteredReportDatas] = useState([]);

  useEffect(() => {
    if (maxEndDate) {
      setSelectedReportDate(dayjs(maxEndDate));
    }
  }, [maxEndDate, setSelectedReportDate]);

  useEffect(() => {
    const datas = creditRatingData?.map(({ name, ticker, fieldInfo }) =>
      createData(name, ticker, fieldInfo),
    );
    const filteredData = datas
      ?.filter((data) => selectedNames.includes(data.name))
      ?.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredReportDatas(filteredData);
  }, [selectedNames, creditRatingData]);

  const parsedMinStartDate = minStartDate ? dayjs(minStartDate) : null;
  const [names, setNames] = useState([]);

  useEffect(() => {
    if (selectedReportDate) {
      setNames(dateWithNames?.[selectedReportDate?.format("YYYY-MM-DD")]);
      setSelectedNames([]);
    }
  }, [dateWithNames, selectedReportDate]);

  const mappedNames = [...(names || []).map(mapName)];

  return (
    <div className={classes.page}>
      <Grid container item spacing={2}>
        <Card
          style={{
            padding: "16px",
            width: "100%",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id={"report-date"}>Report Date</InputLabel>
              <Box display="flex" alignItems="center" gap={2} mb={0}>
                {creditRatingFiltersLoading ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Skeleton variant="rectangular" width={140} height={40} />
                  </Box>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label=""
                      slotProps={{
                        textField: {
                          size: "small",
                          style: { width: 150 },
                        },
                      }}
                      value={selectedReportDate}
                      onChange={(newValue) => {
                        setSelectedReportDate(newValue);
                      }}
                      minDate={parsedMinStartDate}
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                  </LocalizationProvider>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiSelectOption
                selectedOptions={selectedNames}
                setSelectedOptions={setSelectedNames}
                options={mappedNames}
                label="Bank Names"
                labelId="bank-names"
                placeholder={"Select Bank Names"}
                dropdownWidth={120}
                isLoading={creditRatingFiltersLoading}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} style={{ marginTop: "22px" }}>
              <CreditRatingExport
                columns={columns}
                datas={filteredReportDatas}
                disabled={
                  creditRatingDataLoading ||
                  !selectedReportDate ||
                  !filteredReportDatas ||
                  !filteredReportDatas.length
                }
              />
            </Grid>
          </Grid>
        </Card>

        <Grid item xs={12} md={12} lg={12}>
          {selectedReportDate &&
            selectedNames &&
            selectedNames?.length !== 0 && (
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  margin: "10px",
                  color: "#6a6a6a",
                }}
              >
                Showing Credit Rating report on{" "}
                {selectedReportDate?.format("YYYY-MM-DD")} for{" "}
                {mappedNames?.length === selectedNames?.length
                  ? "All"
                  : selectedNames.map((name, index) => (
                      <span key={index}>
                        {capitalizeFirstLetterOfEachWord(name)}
                        {index < selectedNames.length - 1 ? ", " : ""}
                      </span>
                    ))}{" "}
                Banks.
              </Typography>
            )}
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          {!selectedReportDate || !selectedNames || !selectedNames?.length ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                Please select the filters to see the appropriate Credit Rating
                data.
              </Typography>
            </Item>
          ) : !creditRatingDataLoading && creditRatingDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!creditRatingDataLoading && !filteredReportDatas) ||
            (!creditRatingDataLoading && !filteredReportDatas?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <Item>
              <ColumnGroupingTable
                columns={columns}
                datas={filteredReportDatas}
                loading={creditRatingDataLoading}
                freezeParentColumnCount={2}
                rowSpanKey="name"
              />
            </Item>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 12 }} sx={{ display: "none" }}>
          <Item>
            <WhatWeDo />
          </Item>
        </Grid>
      </Grid>
    </div>
  );
};

export default CreditRatingView;
